import { s } from '@/theme/tokens';
import { Box, Text, Tooltip, formatStatusLabel, resolveStatusChipTone } from '@/ui/primitives';
import { Chip } from '@/ui/primitives/Chip';
import { formatDate } from '../../../formatters/formatDate';
import { formatDuration } from '../../../formatters/formatDuration';
import { formatRelativeTime } from '../../../formatters/formatRelativeTime';
import { Stack } from '../../../layout/Stack';
import { buildTimestampMetaChip } from '../tableBuilders';
import { resolveDateTimeLabel } from './shared';
import type { StatusChipStatus } from '@/ui/primitives';
import type { SxProps, Theme } from '@/ui/primitives';
import type { ReactElement } from 'react';

export type BuildSourceMetaCellProps = {
  source: string | null;
  branch: string | null;
  commitSha: string | null;
};

export type StartedFinishedMetaCellProps = {
  startedAt: Date | string | null;
  finishedAt: Date | string | null;
};

export type DurationTimestampMetaProps = {
  durationMs?: number | null;
  timestampValue?: Date | string | null;
};

export type StatusMetaCellProps = {
  status?: StatusChipStatus | null;
  durationMs?: number | null;
  timestampValue?: Date | string | null;
  onStatusClick?: () => void;
  alignStart?: boolean;
  plainMeta?: boolean;
  containerSx?: SxProps<Theme>;
};

const spacingSm = s('sm');

export const BuildSourceMetaCell = ({
  source,
  branch,
  commitSha,
}: BuildSourceMetaCellProps): ReactElement => {
  const sourceLabel = source ?? '-';
  const branchLabel = branch ?? '-';
  const commitLabel = commitSha?.slice(0, 8) ?? '-';
  return (
    <Box sx={{ pt: spacingSm, width: '100%', minWidth: 0 }}>
      <Stack gap="sm">
        <Chip size="xs" label={sourceLabel} tone="neutral" />
        <Chip size="xs" label={branchLabel} tone="neutral" />
        <Chip size="xs" label={commitLabel} tone="neutral" />
      </Stack>
    </Box>
  );
};

export const StartedFinishedMetaCell = ({
  startedAt,
  finishedAt,
}: StartedFinishedMetaCellProps): ReactElement => {
  return (
    <Box sx={{ pt: spacingSm, width: '100%', minWidth: 0 }}>
      <Stack gap="sm">
        <Chip size="xs" label={resolveDateTimeLabel(startedAt)} tone="neutral" />
        <Chip size="xs" label={resolveDateTimeLabel(finishedAt)} tone="neutral" />
      </Stack>
    </Box>
  );
};

export const DurationTimestampMetaCell = ({
  durationMs,
  timestampValue,
}: DurationTimestampMetaProps): ReactElement => {
  const durationLabel = durationMs ? formatDuration(durationMs) : '';
  const timestampLabel = formatRelativeTime(timestampValue);
  const timestampTitle = formatDate(timestampValue, { includeTime: true });
  return (
    <Box sx={{ pt: spacingSm, width: '100%', minWidth: 0 }}>
      <Stack gap="sm">
        {durationLabel ? <Chip size="xs" label={durationLabel} tone="neutral" /> : null}
        {timestampLabel
          ? buildTimestampMetaChip({ label: timestampLabel, title: timestampTitle })
          : null}
      </Stack>
    </Box>
  );
};

export const StatusMetaCell = ({
  status,
  durationMs,
  timestampValue,
  onStatusClick,
  alignStart = false,
  plainMeta = false,
  containerSx,
}: StatusMetaCellProps): ReactElement => {
  const statusLabel = formatStatusLabel(status) || '-';
  const statusTone = resolveStatusChipTone(status);
  const durationLabel = durationMs ? formatDuration(durationMs) : '';
  const timestampLabel = formatRelativeTime(timestampValue);
  const timestampTitle = formatDate(timestampValue, { includeTime: true });
  const isClickable = Boolean(onStatusClick);
  return (
    <Box
      sx={[
        { pt: spacingSm, width: '100%', minWidth: 0 },
        ...(containerSx ? (Array.isArray(containerSx) ? containerSx : [containerSx]) : []),
      ]}
    >
      <Stack gap="sm" align={alignStart ? 'flex-start' : undefined}>
        <Chip
          size="xs"
          label={statusLabel}
          tone={statusTone}
          clickable={isClickable}
          onClick={
            isClickable
              ? (event) => {
                  event.stopPropagation();
                  onStatusClick?.();
                }
              : undefined
          }
        />
        {durationLabel ? (
          plainMeta ? (
            <Text role="captionSmall" color="textSecondary">
              {durationLabel}
            </Text>
          ) : (
            <Chip size="xs" label={durationLabel} tone="neutral" />
          )
        ) : null}
        {timestampLabel ? (
          plainMeta ? (
            <Tooltip title={timestampTitle || ''} disableHoverListener={!timestampTitle} arrow>
              <Text
                role="captionSmall"
                color="textSecondary"
                sx={{
                  display: 'block',
                  fontSize: '13px',
                  minWidth: 0,
                  maxWidth: '100%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {timestampLabel}
              </Text>
            </Tooltip>
          ) : (
            buildTimestampMetaChip({ label: timestampLabel, title: timestampTitle })
          )
        ) : null}
      </Stack>
    </Box>
  );
};
